import React, { useMemo, useState } from 'react';
import CarteAffectation from './CarteAffectation';
import { PALETTE, libelleDept } from './affectationColors';
import './AffectationApercu.css';

// Forme minimale d'un lot_contact (compatible avec ProfileData.lot_contacts de l'annuaire).
interface ApercuInterlocuteur {
  id: number;
  nom: string;
  prenom: string;
  fonction?: string | null;
  email?: string | null;
  telephone?: string | null;
}
interface ApercuLotContact {
  lot_technique: { id: number; nom: string; code: string };
  departement: { id: number; nom: string };
  interlocuteurs: ApercuInterlocuteur[];
}

interface AffectationApercuProps {
  lotContacts: ApercuLotContact[];
}

const EMPTY_SELECTION: Set<number> = new Set();
const NOOP = (): void => undefined;

const formatEmail = (email?: string | null): string => (email ? email.toLowerCase() : '');
const formatPhone = (phone?: string | null): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  let n = digits;
  if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
  else return phone;
  return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};
const nomComplet = (i: ApercuInterlocuteur): string =>
  `${i.prenom ? i.prenom.charAt(0).toUpperCase() + i.prenom.slice(1).toLowerCase() + ' ' : ''}${(i.nom || '').toUpperCase()}`.trim() || `#${i.id}`;

/**
 * Apercu READ-ONLY de l'affectation des membres sur les lots (modale annuaire). Carte uniquement.
 * Derive la donnee de `lot_contacts` : aucun appel API. Menu deroulant lot + filtre departement.
 * Infos interlocuteurs (4 colonnes) en haut, puis la carte ; le filtre surligne le departement.
 */
const AffectationApercu: React.FC<AffectationApercuProps> = ({ lotContacts }) => {
  // Lots techniques distincts, tries par code.
  const lots = useMemo(() => {
    const m = new Map<number, { id: number; nom: string; code: string }>();
    lotContacts.forEach((lc) => m.set(lc.lot_technique.id, lc.lot_technique));
    return Array.from(m.values()).sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }, [lotContacts]);

  const [lotId, setLotId] = useState<number | null>(lots[0]?.id ?? null);
  const [survoleAffecte, setSurvoleAffecte] = useState<number | null>(null);
  const [deptFiltre, setDeptFiltre] = useState<number | null>(null);

  // Interlocuteurs distincts (tous lots) -> couleur (palette) + infos.
  const affectables = useMemo(() => {
    const m = new Map<number, ApercuInterlocuteur>();
    lotContacts.forEach((lc) => lc.interlocuteurs.forEach((i) => m.set(i.id, i)));
    return Array.from(m.values());
  }, [lotContacts]);
  const couleurParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    affectables.forEach((a, i) => m.set(a.id, PALETTE[i % PALETTE.length]));
    return m;
  }, [affectables]);
  const nomParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    affectables.forEach((a) => m.set(a.id, nomComplet(a)));
    return m;
  }, [affectables]);
  const infoParAffecte = useMemo(() => {
    const m = new Map<number, ApercuInterlocuteur>();
    affectables.forEach((a) => m.set(a.id, a));
    return m;
  }, [affectables]);
  // Coordonnees (fonction / email / tel) par affecte, pour l'infobulle de la carte.
  const coordParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    affectables.forEach((a) => {
      const c = [a.fonction, formatEmail(a.email), formatPhone(a.telephone)].filter(Boolean).join(' · ');
      if (c) m.set(a.id, c);
    });
    return m;
  }, [affectables]);

  // Departements du lot choisi -> son affecte (1er interlocuteur de la cellule).
  const affecteParDept = useMemo(() => {
    const m = new Map<number, number | null>();
    lotContacts
      .filter((lc) => lc.lot_technique.id === lotId)
      .forEach((lc) => m.set(lc.departement.id, lc.interlocuteurs[0]?.id ?? null));
    return m;
  }, [lotContacts, lotId]);

  // Departements (ids) couverts par chaque interlocuteur (pour filtrer les cartes par departement).
  const deptsParAffecte = useMemo(() => {
    const m = new Map<number, number[]>();
    Array.from(affecteParDept.entries()).forEach(([deptId, affecteId]) => {
      if (affecteId == null) return;
      const arr = m.get(affecteId) ?? [];
      arr.push(deptId);
      m.set(affecteId, arr);
    });
    return m;
  }, [affecteParDept]);

  // Interlocuteurs effectivement presents sur ce lot.
  const affectesUtilises = useMemo(() => {
    const ids = Array.from(new Set(Array.from(affecteParDept.values()).filter((v): v is number => v != null)));
    return ids.map((id) => infoParAffecte.get(id)).filter((a): a is ApercuInterlocuteur => a != null);
  }, [affecteParDept, infoParAffecte]);

  // Departements du lot (options du menu deroulant de filtre), tries.
  const deptsDuLot = useMemo(() => Array.from(affecteParDept.keys()).sort((a, b) => a - b), [affecteParDept]);

  // Filtre : le departement choisi (ou null = tous).
  const matchIds = useMemo(() => (deptFiltre != null ? new Set([deptFiltre]) : null), [deptFiltre]);

  // Interlocuteurs affiches : tous, ou ceux couvrant le departement filtre.
  const affectesAffiches = useMemo(() => {
    if (!matchIds) return affectesUtilises;
    return affectesUtilises.filter((a) => (deptsParAffecte.get(a.id) ?? []).some((d) => matchIds.has(d)));
  }, [matchIds, affectesUtilises, deptsParAffecte]);

  if (lots.length === 0) {
    return <p className="readonly-empty">Aucune affectation enregistrée.</p>;
  }

  return (
    <div className="affectation-apercu">
      <div className="affectation-apercu-toolbar">
        <label className="affectation-apercu-field">
          <span>Lot technique</span>
          <select value={lotId ?? ''} onChange={(e) => { setLotId(Number(e.target.value)); setDeptFiltre(null); }}>
            {lots.map((l) => (
              <option key={l.id} value={l.id}>{l.code ? `${l.code} - ${l.nom}` : l.nom}</option>
            ))}
          </select>
        </label>
        <label className="affectation-apercu-field">
          <span>Département</span>
          <select value={deptFiltre ?? ''} onChange={(e) => setDeptFiltre(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Tous les départements</option>
            {deptsDuLot.map((id) => (
              <option key={id} value={id}>{libelleDept(id)}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Infos des interlocuteurs EN HAUT (4 colonnes) ; la carte montre leurs departements. */}
      {affectesAffiches.length > 0 && (
        <div className="affectation-apercu-interlos">
          {affectesAffiches.map((a) => (
            <div
              key={a.id}
              className="affectation-apercu-interlo"
              onMouseEnter={() => setSurvoleAffecte(a.id)}
              onMouseLeave={() => setSurvoleAffecte(null)}
            >
              <span className="affectation-apercu-pastille" style={{ background: couleurParAffecte.get(a.id) }} />
              <div className="affectation-apercu-interlo-info">
                <span className="affectation-apercu-interlo-nom">
                  {nomComplet(a)}{a.fonction ? ` — ${a.fonction}` : ''}
                </span>
                {(a.email || a.telephone) && (
                  <span className="affectation-apercu-interlo-contact">
                    {[formatEmail(a.email), formatPhone(a.telephone)].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="affectation-apercu-mapcard">
        <CarteAffectation
          affecteParDept={affecteParDept}
          couleurParAffecte={couleurParAffecte}
          nomParAffecte={nomParAffecte}
          coordParAffecte={coordParAffecte}
          selection={EMPTY_SELECTION}
          onToggle={NOOP}
          survoleAffecte={survoleAffecte}
          surligneDepts={matchIds}
          readOnly
        />
      </div>
    </div>
  );
};

export default AffectationApercu;
